import React from "react";
import moment from "moment";
import parsePhoneNumberFromString from "libphonenumber-js";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import { useResolveSubmittedBy } from "../../../../../../partner/dashboard/projects/project-details/integration-steps/shared/useResolveSubmittedBy";
import "./PartnerDetailsView.scss";

interface PartnerDetailsViewProps {
  partnerDetails: {
    name: string;
    email: string;
    accountManagerEmail: string;
    type: any[];
    potential: string;
    website: string;
    description: string;
    region: string;
    revenueStream: string;
    country: string;
    state: string;
    countryCode: string;
    phoneNumber: string;
    streetAddress: string;
    partnerVertical: string[];
    partnerTier: string;
    registrationDate: Date | null;
  };
  partnerDetailsInitial: any;
}

const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .split(/[\s,_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const PartnerDetailsView: React.FC<PartnerDetailsViewProps> = ({
  partnerDetails,
  partnerDetailsInitial,
}) => {
  const getPartnerTierName = () => {
    const fromPartnerDetails =
      partnerDetailsInitial?.partner_tier ||
      partnerDetailsInitial?.service_tier ||
      partnerDetails?.partnerTier;
    return fromPartnerDetails || "—";
  };

  const formatPhoneNumber = (countryCode: string, phoneNumber: string) => {
    if (!phoneNumber) return "—";
    const parsedPhoneNumber = parsePhoneNumberFromString(
      countryCode + phoneNumber
    );
    return parsedPhoneNumber
      ? parsedPhoneNumber.formatInternational()
      : `${countryCode} ${phoneNumber}`;
  };

  const partnerTypeLabel =
    partnerDetails.type && partnerDetails.type.length > 0
      ? partnerDetails.type.map((type) => toTitleCase(String(type))).join(", ")
      : "—";

  const partnerVerticalLabel =
    partnerDetails.partnerVertical.length > 0
      ? partnerDetails.partnerVertical.map((v) => toTitleCase(v)).join(", ")
      : "—";

  const potentialKey = partnerDetails.potential?.toLowerCase().replace(/\s+/g, "-");

  const resolveSubmittedByName = useResolveSubmittedBy([
    partnerDetailsInitial?.submitted_by,
  ]);
  const submittedBy = resolveSubmittedByName(partnerDetailsInitial?.submitted_by);

  const lastUpdated = partnerDetailsInitial?.last_updated
    ? moment(partnerDetailsInitial.last_updated).format("DD MMMM YYYY, HH:mm")
    : "—";

  const renderLinkValue = (href: string | null, label: string) => {
    if (!href || label === "—") return <span className="pd-field__value">—</span>;
    return (
      <a className="pd-field__link" href={href} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    );
  };

  const formatWebsiteDisplay = (website: string) => {
    if (!website) return "—";
    return website.replace(/^https?:\/\//i, "").replace(/\/$/, "");
  };

  const websiteHref = partnerDetails.website
    ? partnerDetails.website.match(/^https?:\/\//i)
      ? partnerDetails.website
      : `https://${partnerDetails.website}`
    : null;

  return (
    <div className="partner-details-view">
      <section className="pd-card pd-card--sectioned">
        <header className="pd-card__header">
          <h3>Basic Information</h3>
        </header>
        <div className="pd-card__body">
          <div className="pd-basic-content">
            <div className="pd-field pd-field--description">
              <label>Description</label>
              <div className="pd-description-box">
                <div
                  className="pd-description-box__text"
                  dangerouslySetInnerHTML={{
                    __html: partnerDetails.description || "—",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pd-card pd-card--sectioned">
        <header className="pd-card__header">
          <h3>Business Details</h3>
          <p>Partner's classification, market segment, partnership level, and revenue stream</p>
        </header>
        <div className="pd-card__body">
          <div className="pd-business-grid">
            <div className="pd-field pd-field--partner-type">
              <label>Partner Type</label>
              <span className="pd-field__value">{partnerTypeLabel}</span>
              {partnerDetails.potential && (
                <span
                  className={`pd-badge pd-badge--potential pd-badge--${potentialKey}`}
                >
                  {partnerDetails.potential.toUpperCase()}
                </span>
              )}
            </div>
            <div className="pd-field">
              <label>Partner Vertical</label>
              <span className="pd-field__value">{partnerVerticalLabel}</span>
            </div>
            <div className="pd-field">
              <label>Partner Tier</label>
              <span className="pd-badge pd-badge--tier">{getPartnerTierName()}</span>
            </div>
            <div className="pd-field">
              <label>Revenue Stream</label>
              <span className="pd-field__value">
                {partnerDetails.revenueStream || "—"}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="pd-contact-block">
        <section className="pd-card pd-card--sectioned">
          <header className="pd-card__header">
            <h3>Contact Information</h3>
            <p>Emails, website, phone &amp; location</p>
          </header>

          <div className="pd-card__body">
            <div className="pd-contact-grid">
              <div className="pd-contact-item">
                <div className="pd-contact-item__label-row">
                  <div className="pd-contact-item__icon">
                    <Iconography icon="envelop" size={IconographySizes.Small} />
                  </div>
                  <label>HID Account Manager</label>
                </div>
                {partnerDetails.accountManagerEmail ? (
                  <a
                    className="pd-field__link"
                    href={`mailto:${partnerDetails.accountManagerEmail}`}
                  >
                    {partnerDetails.accountManagerEmail}
                  </a>
                ) : (
                  <span className="pd-field__value">—</span>
                )}
              </div>
              <div className="pd-contact-item">
                <div className="pd-contact-item__label-row">
                  <div className="pd-contact-item__icon">
                    <Iconography icon="envelop" size={IconographySizes.Small} />
                  </div>
                  <label>Partner Email</label>
                </div>
                {partnerDetails.email ? (
                  <a
                    className="pd-field__link"
                    href={`mailto:${partnerDetails.email}`}
                  >
                    {partnerDetails.email}
                  </a>
                ) : (
                  <span className="pd-field__value">—</span>
                )}
              </div>
              <div className="pd-contact-item">
                <div className="pd-contact-item__label-row">
                  <div className="pd-contact-item__icon">
                    <Iconography icon="globe" size={IconographySizes.Small} />
                  </div>
                  <label>Website</label>
                </div>
                {renderLinkValue(
                  websiteHref,
                  formatWebsiteDisplay(partnerDetails.website)
                )}
              </div>
              <div className="pd-contact-item">
                <div className="pd-contact-item__label-row">
                  <div className="pd-contact-item__icon">
                    <Iconography icon="phone" size={IconographySizes.Small} />
                  </div>
                  <label>Phone Number</label>
                </div>
                <span className="pd-field__value">
                  {formatPhoneNumber(
                    partnerDetails.countryCode,
                    partnerDetails.phoneNumber
                  )}
                </span>
              </div>

              <div className="pd-contact-divider" />

              <div className="pd-field pd-field--plain">
                <label>Country</label>
                <span className="pd-field__value">
                  {partnerDetails.country || "—"}
                </span>
              </div>
              <div className="pd-field pd-field--plain">
                <label>State</label>
                <span className="pd-field__value">
                  {partnerDetails.state || "—"}
                </span>
              </div>
              <div className="pd-field pd-field--plain">
                <label>Region</label>
                <span className="pd-field__value">
                  {partnerDetails.region || "—"}
                </span>
              </div>

              <div className="pd-contact-item pd-contact-item--address">
                <div className="pd-contact-item__label-row">
                  <div className="pd-contact-item__icon">
                    <Iconography icon="locationDot" size={IconographySizes.Small} />
                  </div>
                  <label>Street Address</label>
                </div>
                <span className="pd-field__value">
                  {partnerDetails.streetAddress || "—"}
                </span>
              </div>
            </div>
          </div>
        </section>

        <footer className="pd-meta-bar">
          <div className="pd-meta-bar__item">
            <label>Submitted By</label>
            <span>{submittedBy}</span>
          </div>
          <div className="pd-meta-bar__item pd-meta-bar__item--right">
            <label>Last Updated On</label>
            <span>{lastUpdated}</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PartnerDetailsView;
